import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { Seo, breadcrumbLd, ORGANIZATION_LD } from "./Seo";
import { Reveal } from "./shared";

export default function ServicePage({ data }) {
    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "Service",
            name: data.h1,
            serviceType: data.serviceType,
            provider: { "@type": "Organization", name: "Flowtech", url: "https://flowtechsolutions.site" },
            areaServed: "United States",
            description: data.seoDesc,
            offers: {
                "@type": "Offer",
                priceSpecification: { "@type": "UnitPriceSpecification", price: "299", priceCurrency: "USD", unitText: "MONTH", description: "Plans start at $299/month with no setup fee" },
            },
        },
        ORGANIZATION_LD,
        breadcrumbLd([{ name: "Home", path: "/" }, { name: data.h1, path: `/${data.slug}` }]),
    ];

    return (
        <article className="pt-32 sm:pt-40 pb-24 sm:pb-32 relative overflow-hidden" data-testid={`service-page-${data.slug}`}>
            <Seo title={data.seoTitle} description={data.seoDesc} path={`/${data.slug}`} jsonLd={jsonLd} />
            <div className="absolute top-0 left-1/4 w-[500px] h-[400px] bg-cyan-500/[0.06] blur-[140px] rounded-full pointer-events-none" />
            <div className="container-x relative max-w-4xl">
                <Reveal>
                    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-slate-500 mb-8">
                        <Link to="/" className="hover:text-cyan-300 transition-colors duration-300">Home</Link>
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-slate-400">{data.h1}</span>
                    </nav>
                </Reveal>
                <Reveal>
                    <p className="ff-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-4">{data.eyebrow}</p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.1]">{data.h1}</h1>
                </Reveal>
                {data.intro.map((p, i) => (
                    <Reveal key={i} delay={0.14 + i * 0.06}>
                        <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed">{p}</p>
                    </Reveal>
                ))}
                <div className="mt-14 grid sm:grid-cols-2 gap-5">
                    {data.points.map((pt, i) => (
                        <Reveal key={pt.title} delay={i * 0.08} className="h-full">
                            <div className="glass card-hover rounded-3xl p-7 h-full">
                                <div className="w-9 h-9 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
                                    <Check className="w-4 h-4 text-cyan-300" />
                                </div>
                                <h2 className="mt-5 text-lg font-bold text-white tracking-tight">{pt.title}</h2>
                                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{pt.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.1}>
                    <p className="mt-12 text-base sm:text-lg text-slate-300 leading-relaxed">{data.closing}</p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="mt-10 glass rounded-3xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
                        <div>
                            <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">See How Flowtech Can Help</h2>
                            <p className="mt-2 text-sm text-slate-400 max-w-md">
                                Plans start at $299/month with no setup fee. Tell us about your business and we'll show you exactly what can be automated.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3 shrink-0">
                            <Link to="/#book" className="btn-primary" data-testid={`service-cta-${data.slug}`}>
                                Start a Free Chat
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/#demo" className="btn-ghost">Watch It Work</Link>
                        </div>
                    </div>
                </Reveal>
            </div>
        </article>
    );
}
